import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subscription, catchError, map, throwError } from 'rxjs';

import { ApiService } from '../../../shared/services/api.service';
import { AuthService } from '../../../shared/services/auth.service';
import { PROFILE_COPY } from '../profile.copy';
import type { ProfileOrderDetail, ProfileOrdersPage, ProfilePrepurchase } from './profile-orders.models';

/**
 * Fallo tipado de «Mis pedidos»: el componente distingue el 404 (pinta el texto del servidor) del resto.
 * `unauthorized` ya ha lanzado `auth.me()`: la sesión se limpia y el contenedor vuelve a «Inicia sesión…».
 */
export type ProfileOrdersFailure =
  | { kind: 'not-found'; message: string }
  | { kind: 'unauthorized' }
  | { kind: 'error' };

/**
 * `GET /profile/orders`, `GET /profile/orders/{id}` (A2 de B10) y `GET /profile/prepurchases` (A2b),
 * `docs/api/profile-api.md` §A2 y §A2b.
 *
 * - **Solo en navegador** (§5.6.6): en SSR no hay petición; los métodos completan sin emitir.
 * - **401 → `auth.me()`** (§5.6.7), que ante error limpia tokens y `currentUser`. Nunca `logout()`, que navega a `/`.
 * - **Sin estado**: ni caché de páginas ni de detalles. Todo lo que se pinta pertenece a una cuenta y lo guarda el
 *   componente atado a `currentUser()?.id`; un `root` con caché enseñaría los pedidos de A a B.
 */
@Injectable({ providedIn: 'root' })
export class ProfileOrdersService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  /** Recuperación de sesión tras un 401: una sola en vuelo aunque fallen a la vez listado y detalle. */
  private recovery: Subscription | null = null;

  list(page: number): Observable<ProfileOrdersPage> {
    if (!this.isBrowser) return EMPTY;
    return this.api
      .get<ProfileOrdersPage>('/profile/orders', { page })
      .pipe(catchError((err: unknown) => this.fail(err)));
  }

  /** `GET /profile/prepurchases` (A2b): sin paginar, desenvuelve `data`. Sin verificar la API manda `[]`. */
  prepurchases(): Observable<ProfilePrepurchase[]> {
    if (!this.isBrowser) return EMPTY;
    return this.api.get<{ data?: ProfilePrepurchase[] }>('/profile/prepurchases').pipe(
      map((res) => res.data ?? []),
      catchError((err: unknown) => this.fail(err)),
    );
  }

  /** `id` como CADENA decimal ya validada: convertirla a `Number` redondea por encima de 2^53 y pediría otro pedido. */
  detail(id: string): Observable<ProfileOrderDetail> {
    if (!this.isBrowser) return EMPTY;
    return this.api.get<{ data: ProfileOrderDetail }>(`/profile/orders/${id}`).pipe(
      map((res) => res.data),
      catchError((err: unknown) => this.fail(err)),
    );
  }

  private fail(err: unknown): Observable<never> {
    const status = err instanceof HttpErrorResponse ? err.status : 0;

    if (status === 401) {
      this.recoverSession();
      return throwError((): ProfileOrdersFailure => ({ kind: 'unauthorized' }));
    }

    if (status === 404) {
      const body = (err as HttpErrorResponse).error as { error?: unknown } | null;
      const message = typeof body?.error === 'string' && body.error ? body.error : PROFILE_COPY.ORDER_DETAIL.NOT_FOUND;
      return throwError((): ProfileOrdersFailure => ({ kind: 'not-found', message }));
    }

    return throwError((): ProfileOrdersFailure => ({ kind: 'error' }));
  }

  /**
   * §5.6.7: `auth.me()`, que ante error limpia la sesión. Si ya hay una en vuelo no se lanza otra. No queda
   * huérfana: `AuthService` cancela los `me()` de una sesión anterior al salir o entrar, y una respuesta que
   * llegue tarde no escribe en la sesión nueva (Astra W1 H1).
   */
  private recoverSession(): void {
    if (this.recovery && !this.recovery.closed) return;
    this.recovery = this.auth.me().subscribe();
  }
}
